import {DatePicker} from '@github-ui/date-picker'
import {hpcAttrs} from '@github-ui/hpc'
import {SidebarPageLayout} from '@github-ui/layout-primitives/SidebarPageLayout'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import type {CreateMilestoneInput} from '@github-ui/server-persisted-queries/types/createRepositoryMilestoneMutation'
import {Banner, Button, FormControl, Heading, Textarea, TextInput} from '@primer/react'
import {type Dispatch, type ReactNode, type Ref, type SetStateAction, useCallback, useRef, useState} from 'react'

import {LABELS} from '../constants/labels'
import type {RepositoryNode} from './milestone-form-query-types'
import styles from './MilestoneForm.module.css'

function dueOnFromDate(date: Date | null) {
  return date ? new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString() : null
}

export type MilestoneFormFragment = Pick<RepositoryNode, 'id'>

export type MilestoneInitialValues = {
  id?: string
  title?: string
  description?: string | null
  dueOn?: string | null
}

export type MilestoneFormProps = {
  repository: MilestoneFormFragment
  onCancel?: () => void
  formTitle?: string
  formDescription?: ReactNode
  formSubmitLabel?: string
  submissionErrors?: string | null
  initialValues?: MilestoneInitialValues
  isPending?: boolean
  isComplete?: boolean
  formRef?: Ref<HTMLFormElement>
  onSubmit: (input: CreateMilestoneInput, setIsSubmitting: Dispatch<SetStateAction<boolean>>) => void
  onToggleMilestoneState?: (
    e: React.MouseEvent<HTMLButtonElement>,
    setIsSubmitting: Dispatch<SetStateAction<boolean>>,
    input: MilestoneInitialValues,
  ) => void
  toggleStateLabel?: string
}

export function MilestoneForm({
  repository,
  onCancel,
  formTitle,
  formDescription,
  formSubmitLabel = LABELS.createMilestone,
  onSubmit,
  submissionErrors,
  initialValues,
  isPending = false,
  isComplete = false,
  formRef,
  onToggleMilestoneState,
  toggleStateLabel,
}: MilestoneFormProps) {
  const [title, setTitle] = useState(initialValues?.title || '')
  const [description, setDescription] = useState(initialValues?.description || '')
  const [dueDate, setDueDate] = useState<Date | null>(() => {
    if (initialValues?.dueOn) {
      const utcDate = new Date(initialValues.dueOn)
      return new Date(utcDate.getUTCFullYear(), utcDate.getUTCMonth(), utcDate.getUTCDate())
    }
    return null
  })
  const [isLocallySubmitting, setIsSubmitting] = useState(false)
  const isSubmitting = isLocallySubmitting || isPending
  const isFormLocked = isSubmitting || isComplete
  const [titleValidationError, setTitleValidationError] = useState<string | null>(null)
  const sidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()

  const titleInputRef = useRef<HTMLInputElement>(null)

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (isFormLocked) {
        return
      }

      setIsSubmitting(true)
      setTitleValidationError(null)

      if (!title.trim()) {
        setTitleValidationError(LABELS.titleRequired)
        titleInputRef.current?.focus()
        setIsSubmitting(false)
        return
      }

      const input: CreateMilestoneInput = {
        clientMutationId: undefined,
        repositoryId: repository.id,
        title: title.trim(),
        description: description?.trim(),
        dueOn: dueOnFromDate(dueDate),
      }

      onSubmit(input, setIsSubmitting)
      return
    },
    [description, dueDate, onSubmit, repository, title, isFormLocked],
  )

  const onDatePickerChange = (date: Date | null) => {
    setDueDate(date)
  }

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value)
    if (titleValidationError) {
      setTitleValidationError(null)
    }
  }

  return (
    <div className={styles.formContainer} id="milestone-create-form" {...hpcAttrs()}>
      {submissionErrors && (
        <Banner
          aria-label="Error"
          title="Error"
          description={submissionErrors}
          variant="critical"
          className={styles.errorBanner}
        />
      )}
      <div>
        {formTitle && !sidebarLayoutEnabled && (
          <div className={styles.titleGroup}>
            <SidebarPageLayout.SidebarTrigger />
            <Heading as="h1" variant="medium" className={styles.milestonePageTitle}>
              {formTitle}
            </Heading>
          </div>
        )}
        {formDescription}
      </div>

      <form ref={formRef} onSubmit={handleSubmit}>
        <div className={styles.formWrapper}>
          <FormControl className={styles.formControl} id="milestone-title" disabled={isFormLocked}>
            <FormControl.Label htmlFor="milestone-title">{LABELS.title}</FormControl.Label>
            <TextInput
              block
              value={title}
              onChange={handleTitleChange}
              placeholder={LABELS.titlePlaceholder}
              ref={titleInputRef}
              aria-required
            />
            {titleValidationError && (
              <FormControl.Validation variant="error">{titleValidationError}</FormControl.Validation>
            )}
          </FormControl>

          <FormControl className={styles.formControl} id="milestone-due-on">
            <FormControl.Label htmlFor="milestone-due-on">
              {LABELS.dueDate} <span className={styles.optionalLabel}>{LABELS.optional}</span>
            </FormControl.Label>
            <DatePicker
              variant="single"
              showClearButton
              value={dueDate}
              onChange={onDatePickerChange}
              disabled={isFormLocked}
              dateFormat="MMM d, yyyy"
              placeholder={LABELS.datePlaceholder}
              anchorClassName={styles.datePickerAnchor}
              fieldName={LABELS.dueDate}
            />
          </FormControl>

          <FormControl className={styles.formControl} id="milestone-description" disabled={isFormLocked}>
            <FormControl.Label htmlFor="milestone-description">
              {LABELS.description} <span className={styles.optionalLabel}>{LABELS.optional}</span>
            </FormControl.Label>
            <Textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={5}
              placeholder={LABELS.descriptionPlaceholder}
              block
            />
          </FormControl>
        </div>
        <div className={`${styles.buttonRow} ${onToggleMilestoneState ? 'flex-justify-between' : 'flex-justify-end'}`}>
          {onToggleMilestoneState && toggleStateLabel && (
            <Button
              onClick={e =>
                onToggleMilestoneState?.(e, setIsSubmitting, {
                  title,
                  description,
                  dueOn: dueOnFromDate(dueDate),
                })
              }
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              {toggleStateLabel}
            </Button>
          )}
          <div className={styles.buttonGroup}>
            <Button type="button" onClick={isSubmitting ? undefined : onCancel} aria-disabled={isSubmitting}>
              {LABELS.cancel}
            </Button>
            <Button type="submit" variant="primary" loading={isSubmitting} aria-disabled={isFormLocked}>
              {formSubmitLabel}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}
